// External Imports
import { useState } from "react";
import Modal from "../UI/Modal";
import Heading from "../UI/Heading";
import { MdClose } from "react-icons/md";
import DetailPair from "../UI/DetailPair";
import { isoDateToString } from "../../util/format-date";
import LinkPair from "../UI/LinkPair";
import LoadingCard from "../UI/LoadingCard";
import NoRecordsFound from "../UI/NoRecordsFound";
import LoadingSpinner from "../UI/LoadingSpinner";
import { requestAdminAPI } from "../../api/posh-api";

// Internal Imports

/** ========================================================================
 * * CustomerScreenings Component
 * This component renders a modal displaying list of customer's screening records.
 * ========================================================================== */

export default function CustomerScreenings({
  open,
  setOpen,
  screenings = [],
  customerId,
  refresh,
}) {
  const [loading, setLoading] = useState(false);

  /* =================== CHECK SCREENING STATUS =================== */
  const startScreening = async () => {
    try {
      setLoading(true);
      await requestAdminAPI("start-screening", {
        customerId,
      });
      refresh();
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  return (
    <Modal open={open} setOpen={setOpen}>
      <div className="grid gap-8 modal">
        <Heading
          title="Customer Screenings"
          text="Different screening providers may have different scoring criteria. As long as the status is 'Approved', the customer has passed the screening."
          isBackArrow={false}
        >
          <MdClose className="icon" onClick={() => setOpen(false)} />
        </Heading>

        {loading ? (
          <LoadingCard columns={5} max={2} />
        ) : screenings.length > 0 ? (
          <div className="list max-h-[600px]">
            {screenings.map((screening, idx) => (
              <div
                key={idx}
                className="p-4 border rounded-md info border-stone-600"
              >
                <DetailPair label="ID" value={screening.id} />
                <DetailPair
                  label="Created"
                  value={isoDateToString(screening.dateCreated)}
                />
                <DetailPair
                  label="Completed"
                  value={isoDateToString(screening.dateCompleted)}
                />
                <DetailPair
                  label="Provider"
                  value={screening.provider.toUpperCase()}
                />
                <DetailPair
                  label="Identity Score"
                  value={screening.kyc}
                  classes={`${
                    Number(screening.kyc) < 40
                      ? "text-red-600 animate-pulse"
                      : "text-green-600"
                  }`}
                />
                <DetailPair
                  label="Risk Score"
                  value={screening.risk}
                />
                {screening?.link && (
                  <LinkPair
                    label="Screening Link"
                    link={
                      screening.provider === "lexisnexis"
                        ? null
                        : screening.link
                    }
                  />
                )}
                <DetailPair
                  label="Status"
                  value={screening.status.toUpperCase()}
                  classes={`${
                    screening.status === "approved"
                      ? "text-green-600"
                      : screening.status === "declined"
                      ? "text-red-600"
                      : "text-yellow-600"
                  }`}
                />
                {screening.provider === "lexisnexis" && (
                  <div className="text-sm lg:col-span-4">
                    <p className="text-stone-400">Identification Issues:</p>
                    <textarea
                      className="w-full form-input-disabled"
                      disabled
                      rows="8"
                      value={screening.document}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <NoRecordsFound />
        )}

        <div className="info">
          {loading ? (
            <LoadingSpinner />
          ) : (
            <button onClick={startScreening} className="btn-primary">
              Start Screening
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
